import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

const defaultDates = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    start_date: start.toISOString().slice(0, 10),
    end_date: end.toISOString().slice(0, 10)
  };
};

const formatMoney = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value).slice(0, 10) : d.toLocaleString();
};
const formatDateOnly = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value).slice(0, 10) : d.toLocaleDateString();
};

const statusColor = (status) => {
  const s = String(status || '').toLowerCase();
  if (['completed', 'paid', 'active', 'approved'].includes(s)) return 'success';
  if (['pending', 'accrued', 'processing', 'expiring_soon'].includes(s)) return 'warning';
  if (['failed', 'refunded', 'expired', 'rejected', 'cancelled', 'reversed'].includes(s)) return 'error';
  return 'default';
};

const SummaryCard = ({ label, value, color }) => (
  <Card sx={{ p: 2, height: '100%' }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ mt: 0.5, color: color || 'inherit' }}>{value}</Typography>
  </Card>
);

const AdminBillingReports = ({
  schools = [],
  agencies = [],
  initialTab = 0,
  initialGroupBy = 'detail',
  initialSchoolId = '',
  initialAgencyId = ''
}) => {
  const dates = useMemo(() => defaultDates(), []);
  const [tab, setTab] = useState(initialTab);
  const [filters, setFilters] = useState({
    ...dates,
    school_id: initialSchoolId ? String(initialSchoolId) : '',
    agency_id: initialAgencyId ? String(initialAgencyId) : '',
    status: 'all',
    search: '',
    group_by: initialGroupBy || 'detail',
    beneficiary_type: 'all'
  });

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, group_by: initialGroupBy || 'detail' }));
  }, [initialGroupBy]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      school_id: String(initialSchoolId || ''),
      agency_id: String(initialAgencyId || '')
    }));
  }, [initialSchoolId, initialAgencyId]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [payments, setPayments] = useState({ items: [], completed_amount: 0, total_rows: 0 });
  const [orders, setOrders] = useState({ items: [], completed_amount: 0, total_rows: 0 });
  const [subscriptions, setSubscriptions] = useState({ items: [], total_rows: 0 });
  const [commissions, setCommissions] = useState({ items: [], total_commission: 0, group_by: 'detail' });
  const [withdrawals, setWithdrawals] = useState({ items: [], pending_amount: 0, total_rows: 0 });
  const [rollup, setRollup] = useState({ items: [], total_rows: 0 });
  const [detail, setDetail] = useState(null);

  const queryString = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== 'all') params.set(key, value);
    });
    return params.toString();
  }, [filters]);

  const authHeaders = () => {
    const token = localStorage.getItem('authToken');
    return { Authorization: `Bearer ${token}` };
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const qs = queryString();

      const endpoints = [
        { key: 'overview', url: `${API_BASE_URL}/admin/billing/overview?${qs}` },
        { key: 'payments', url: `${API_BASE_URL}/admin/billing/payments?${qs}` },
        { key: 'orders', url: `${API_BASE_URL}/admin/billing/orders?${qs}` },
        { key: 'subscriptions', url: `${API_BASE_URL}/admin/billing/subscriptions?${qs}` },
        { key: 'commissions', url: `${API_BASE_URL}/admin/billing/commissions?${qs}` },
        { key: 'withdrawals', url: `${API_BASE_URL}/admin/billing/withdrawals?${qs}` },
        { key: 'rollup', url: `${API_BASE_URL}/admin/billing/school-agency-summary?${qs}` }
      ];

      const results = await Promise.all(
        endpoints.map(async ({ key, url }) => {
          try {
            const res = await fetch(url, { headers: authHeaders() });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
              return { key, error: data.message || `Failed to load ${key}` };
            }
            return { key, data };
          } catch (err) {
            return { key, error: err.message || `Failed to load ${key}` };
          }
        })
      );

      const errors = [];
      results.forEach(({ key, data, error: errMsg }) => {
        if (errMsg) {
          errors.push(`${key}: ${errMsg}`);
          return;
        }
        if (key === 'overview') setOverview(data.summary);
        if (key === 'payments') setPayments(data);
        if (key === 'orders') setOrders(data);
        if (key === 'subscriptions') setSubscriptions(data);
        if (key === 'commissions') setCommissions(data);
        if (key === 'withdrawals') setWithdrawals(data);
        if (key === 'rollup') setRollup(data);
      });

      if (errors.length) {
        setError(errors.join(' | '));
      }
    } catch (err) {
      setError(err.message || 'Failed to load billing reports');
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useAutoRefresh(loadData);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const statusOptionsForTab = () => {
    if (tab === 1) {
      return ['all', 'completed', 'pending', 'failed', 'refunded'];
    }
    if (tab === 2) {
      return ['all', 'completed', 'pending', 'processing', 'failed', 'refunded', 'cancelled'];
    }
    if (tab === 3) {
      return ['all', 'active', 'expired', 'expiring_soon', 'cancelled', 'suspended'];
    }
    if (tab === 4) {
      return ['all', 'accrued', 'approved', 'paid', 'reversed', 'cancelled'];
    }
    if (tab === 5) {
      return ['all', 'pending', 'completed', 'rejected', 'cancelled'];
    }
    return ['all'];
  };

  return (
    <Box>
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Billing & Revenue Reports</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Platform-wide payments, online orders, subscriptions, commissions and withdrawals.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth size="small" type="date" label="From"
              InputLabelProps={{ shrink: true }}
              value={filters.start_date}
              onChange={(e) => updateFilter('start_date', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth size="small" type="date" label="To"
              InputLabelProps={{ shrink: true }}
              value={filters.end_date}
              onChange={(e) => updateFilter('end_date', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>School</InputLabel>
              <Select label="School" value={filters.school_id} onChange={(e) => updateFilter('school_id', e.target.value)}>
                <MenuItem value="">All schools</MenuItem>
                {schools.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Agency</InputLabel>
              <Select label="Agency" value={filters.agency_id} onChange={(e) => updateFilter('agency_id', e.target.value)}>
                <MenuItem value="">All agencies</MenuItem>
                {agencies.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={filters.status} onChange={(e) => updateFilter('status', e.target.value)}>
                {statusOptionsForTab().map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth size="small" label="Search"
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              placeholder="Name / txn / device"
            />
          </Grid>
          {tab === 4 && (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Group by</InputLabel>
                  <Select label="Group by" value={filters.group_by} onChange={(e) => updateFilter('group_by', e.target.value)}>
                    <MenuItem value="detail">Detail ledger</MenuItem>
                    <MenuItem value="date">Date-wise</MenuItem>
                    <MenuItem value="school">School-wise</MenuItem>
                    <MenuItem value="agency">Agency-wise</MenuItem>
                    <MenuItem value="device">Device-wise</MenuItem>
                    <MenuItem value="event">Payment source</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Beneficiary</InputLabel>
                  <Select label="Beneficiary" value={filters.beneficiary_type} onChange={(e) => updateFilter('beneficiary_type', e.target.value)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="school">School</MenuItem>
                    <MenuItem value="agency">Agency</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={loadData} disabled={loading}>
            {loading ? 'Loading…' : 'Apply Filters'}
          </Button>
          <Button
            variant="outlined"
            disabled={loading}
            onClick={() => setFilters({ ...defaultDates(), school_id: '', agency_id: '', status: 'all', search: '', group_by: 'detail', beneficiary_type: 'all' })}
          >
            Reset
          </Button>
        </Stack>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      {loading && !overview ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}><SummaryCard label="Total Revenue" value={formatMoney(overview?.totalRevenue)} color="#0f766e" /></Grid>
            <Grid item xs={6} md={3}><SummaryCard label="Offline (School)" value={formatMoney(overview?.offlineRevenue)} /></Grid>
            <Grid item xs={6} md={3}><SummaryCard label="Online (Parent)" value={formatMoney(overview?.onlineRevenue)} /></Grid>
            <Grid item xs={6} md={3}><SummaryCard label="Commission Accrued" value={formatMoney(overview?.commissionAccrued)} /></Grid>
            <Grid item xs={6} md={3}><SummaryCard label="Active Subscriptions" value={overview?.activeSubscriptions ?? 0} /></Grid>
            <Grid item xs={6} md={3}><SummaryCard label="Expiring in 15 days" value={overview?.expiringSoon ?? 0} color="#b45309" /></Grid>
            <Grid item xs={6} md={3}><SummaryCard label="Commission Paid" value={formatMoney(overview?.commissionPaid)} /></Grid>
            <Grid item xs={6} md={3}><SummaryCard label="Pending Withdrawals" value={overview?.withdrawalsPending ?? 0} color="#b91c1c" /></Grid>
          </Grid>

          <Card>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
            >
              <Tab label="Overview" />
              <Tab label="Offline Payments" />
              <Tab label="Online Orders" />
              <Tab label="Subscriptions" />
              <Tab label="Commissions" />
              <Tab label="Withdrawals" />
              <Tab label="School / Agency" />
            </Tabs>

            <Box sx={{ p: 2 }}>
              {tab === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Collections snapshot</Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow><TableCell>Offline payments</TableCell><TableCell align="right">{overview?.offlinePayments ?? 0}</TableCell></TableRow>
                        <TableRow><TableCell>Online orders</TableCell><TableCell align="right">{overview?.onlineOrders ?? 0}</TableCell></TableRow>
                        <TableRow><TableCell>Pending revenue</TableCell><TableCell align="right">{formatMoney(overview?.pendingRevenue)}</TableCell></TableRow>
                        <TableRow><TableCell>Failed revenue</TableCell><TableCell align="right">{formatMoney(overview?.failedRevenue)}</TableCell></TableRow>
                        <TableRow><TableCell>Refunded</TableCell><TableCell align="right">{formatMoney(overview?.refundedRevenue)}</TableCell></TableRow>
                        <TableRow><TableCell>Expired subscriptions</TableCell><TableCell align="right">{overview?.expiredSubscriptions ?? 0}</TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Top schools (by revenue)</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>School</TableCell>
                          <TableCell>Agency</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(rollup.items || []).slice(0, 8).map((row) => (
                          <TableRow key={row.schoolId}>
                            <TableCell>{row.schoolName}</TableCell>
                            <TableCell>{row.agencyName || '—'}</TableCell>
                            <TableCell align="right">{formatMoney(row.totalRevenue)}</TableCell>
                          </TableRow>
                        ))}
                        {(rollup.items || []).length === 0 && (
                          <TableRow><TableCell colSpan={3}>No revenue in selected period.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>
              )}

              {tab === 1 && (
                <>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Chip label={`Rows: ${payments.total_rows || 0}`} size="small" />
                    <Chip label={`Completed: ${formatMoney(payments.completed_amount)}`} size="small" color="success" variant="outlined" />
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Student</TableCell>
                          <TableCell>School</TableCell>
                          <TableCell>Agency</TableCell>
                          <TableCell>Plan</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(payments.items || []).map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{formatDateOnly(row.paymentDate)}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.studentName || '—'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {[row.standard, row.division].filter(Boolean).join(' / ')}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.schoolName || '—'}</TableCell>
                            <TableCell>{row.agencyName || '—'}</TableCell>
                            <TableCell>{row.planName || '—'}</TableCell>
                            <TableCell>{row.paymentMethod || '—'}</TableCell>
                            <TableCell><Chip size="small" label={row.status} color={statusColor(row.status)} /></TableCell>
                            <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                            <TableCell align="right">
                              <Button size="small" onClick={() => setDetail({ type: 'payment', row })}>View</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {(payments.items || []).length === 0 && (
                          <TableRow><TableCell colSpan={9} align="center">No offline payments found.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {tab === 2 && (
                <>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Chip label={`Rows: ${orders.total_rows || 0}`} size="small" />
                    <Chip label={`Completed: ${formatMoney(orders.completed_amount)}`} size="small" color="success" variant="outlined" />
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Order</TableCell>
                          <TableCell>Parent</TableCell>
                          <TableCell>Device</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Gateway</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(orders.items || []).map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{formatDateOnly(row.paidAt || row.createdAt)}</TableCell>
                            <TableCell>{row.orderNumber}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.parentName || '—'}</Typography>
                              <Typography variant="caption" color="text.secondary">{row.parentPhone || row.parentEmail || ''}</Typography>
                            </TableCell>
                            <TableCell>{row.deviceId || '—'}</TableCell>
                            <TableCell>{row.orderType}</TableCell>
                            <TableCell>{row.paymentGateway || '—'}</TableCell>
                            <TableCell><Chip size="small" label={row.status} color={statusColor(row.status)} /></TableCell>
                            <TableCell align="right">{formatMoney(row.totalAmount)}</TableCell>
                            <TableCell align="right">
                              <Button size="small" onClick={() => setDetail({ type: 'order', row })}>View</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {(orders.items || []).length === 0 && (
                          <TableRow><TableCell colSpan={9} align="center">No online orders found.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {tab === 3 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Student</TableCell>
                        <TableCell>School</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell>End</TableCell>
                        <TableCell>Days left</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Device</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(subscriptions.items || []).map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Typography variant="body2">{row.studentName || '—'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[row.standard, row.division].filter(Boolean).join(' / ')}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.schoolName || '—'}</TableCell>
                          <TableCell>{row.planName || '—'}</TableCell>
                          <TableCell>{formatDateOnly(row.startDate)}</TableCell>
                          <TableCell>{formatDateOnly(row.endDate)}</TableCell>
                          <TableCell>{row.daysLeft}</TableCell>
                          <TableCell><Chip size="small" label={row.status} color={statusColor(row.status)} /></TableCell>
                          <TableCell>{row.deviceId || '—'}</TableCell>
                        </TableRow>
                      ))}
                      {(subscriptions.items || []).length === 0 && (
                        <TableRow><TableCell colSpan={8} align="center">No subscriptions found.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {tab === 4 && (
                <>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Chip label={`Group: ${commissions.group_by || 'detail'}`} size="small" />
                    {commissions.group_by === 'detail' && (
                      <Chip label={`Commission: ${formatMoney(commissions.total_commission)}`} size="small" color="success" variant="outlined" />
                    )}
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {commissions.group_by === 'detail' ? (
                            <>
                              <TableCell>Date</TableCell>
                              <TableCell>Beneficiary</TableCell>
                              <TableCell>Event</TableCell>
                              <TableCell>School</TableCell>
                              <TableCell>Agency</TableCell>
                              <TableCell align="right">Base</TableCell>
                              <TableCell align="right">Rate</TableCell>
                              <TableCell align="right">Commission</TableCell>
                              <TableCell>Status</TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>Group</TableCell>
                              <TableCell align="right">Entries</TableCell>
                              <TableCell align="right">Base</TableCell>
                              <TableCell align="right">Commission</TableCell>
                            </>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(commissions.items || []).map((row, idx) => (
                          commissions.group_by === 'detail' ? (
                            <TableRow key={row.id || idx} hover>
                              <TableCell>{formatDate(row.createdAt)}</TableCell>
                              <TableCell>
                                <Typography variant="body2">{row.beneficiaryName || '—'}</Typography>
                                <Typography variant="caption" color="text.secondary">{row.beneficiaryType}</Typography>
                              </TableCell>
                              <TableCell>{row.eventType}</TableCell>
                              <TableCell>{row.schoolName || '—'}</TableCell>
                              <TableCell>{row.agencyName || '—'}</TableCell>
                              <TableCell align="right">{formatMoney(row.baseAmount)}</TableCell>
                              <TableCell align="right">
                                {row.commissionType === 'fixed' ? formatMoney(row.commissionRate) : `${row.commissionRate}%`}
                              </TableCell>
                              <TableCell align="right">{formatMoney(row.commissionAmount)}</TableCell>
                              <TableCell><Chip size="small" label={row.status} color={statusColor(row.status)} /></TableCell>
                            </TableRow>
                          ) : (
                            <TableRow key={`${row.report_key}-${idx}`} hover>
                              <TableCell>{row.report_label || row.report_key}</TableCell>
                              <TableCell align="right">{row.entry_count}</TableCell>
                              <TableCell align="right">{formatMoney(row.total_base_amount)}</TableCell>
                              <TableCell align="right">{formatMoney(row.total_commission)}</TableCell>
                            </TableRow>
                          )
                        ))}
                        {(commissions.items || []).length === 0 && (
                          <TableRow><TableCell colSpan={9} align="center">No commission entries found.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {tab === 5 && (
                <>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Chip label={`Rows: ${withdrawals.total_rows || 0}`} size="small" />
                    <Chip label={`Pending: ${formatMoney(withdrawals.pending_amount)}`} size="small" color="warning" variant="outlined" />
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Beneficiary</TableCell>
                          <TableCell>Requested by</TableCell>
                          <TableCell>Bank</TableCell>
                          <TableCell>Account</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(withdrawals.items || []).map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{formatDate(row.createdAt)}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.beneficiaryName || '—'}</Typography>
                              <Typography variant="caption" color="text.secondary">{row.beneficiaryType}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.requestedByName || '—'}</Typography>
                              <Typography variant="caption" color="text.secondary">{row.requestedByEmail || ''}</Typography>
                            </TableCell>
                            <TableCell>{row.bankName || '—'}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.accountNumber || '—'}</Typography>
                              <Typography variant="caption" color="text.secondary">{row.ifscCode || ''}</Typography>
                            </TableCell>
                            <TableCell><Chip size="small" label={row.status} color={statusColor(row.status)} /></TableCell>
                            <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                          </TableRow>
                        ))}
                        {(withdrawals.items || []).length === 0 && (
                          <TableRow><TableCell colSpan={7} align="center">No withdrawals found.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {tab === 6 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>School</TableCell>
                        <TableCell>Agency</TableCell>
                        <TableCell align="right">Offline payments</TableCell>
                        <TableCell align="right">Offline ₹</TableCell>
                        <TableCell align="right">Online orders</TableCell>
                        <TableCell align="right">Online ₹</TableCell>
                        <TableCell align="right">Total ₹</TableCell>
                        <TableCell align="right">Commission ₹</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(rollup.items || []).map((row) => (
                        <TableRow key={row.schoolId} hover>
                          <TableCell>{row.schoolName}</TableCell>
                          <TableCell>{row.agencyName || '—'}</TableCell>
                          <TableCell align="right">{row.paymentCount}</TableCell>
                          <TableCell align="right">{formatMoney(row.paymentRevenue)}</TableCell>
                          <TableCell align="right">{row.orderCount}</TableCell>
                          <TableCell align="right">{formatMoney(row.orderRevenue)}</TableCell>
                          <TableCell align="right"><strong>{formatMoney(row.totalRevenue)}</strong></TableCell>
                          <TableCell align="right">{formatMoney(row.commissionAmount)}</TableCell>
                        </TableRow>
                      ))}
                      {(rollup.items || []).length === 0 && (
                        <TableRow><TableCell colSpan={8} align="center">No school/agency revenue in this period.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Card>
        </>
      )}

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{detail?.type === 'order' ? 'Order details' : 'Payment details'}</DialogTitle>
        <DialogContent dividers>
          {detail?.type === 'payment' && detail.row && (
            <Grid container spacing={1}>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Student</Typography><Typography>{detail.row.studentName}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">School</Typography><Typography>{detail.row.schoolName || '—'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Agency</Typography><Typography>{detail.row.agencyName || '—'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Plan</Typography><Typography>{detail.row.planName || '—'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Amount</Typography><Typography>{formatMoney(detail.row.amount)}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Tax</Typography><Typography>{formatMoney(detail.row.taxAmount)} ({detail.row.taxPercentage}%)</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Method</Typography><Typography>{detail.row.paymentMethod || '—'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Status</Typography><Typography>{detail.row.status}</Typography></Grid>
              <Grid item xs={12}><Typography variant="caption" color="text.secondary">Transaction</Typography><Typography>{detail.row.transactionId || '—'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Sub start</Typography><Typography>{formatDateOnly(detail.row.subscriptionStart)}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Sub end</Typography><Typography>{formatDateOnly(detail.row.subscriptionEnd)}</Typography></Grid>
            </Grid>
          )}
          {detail?.type === 'order' && detail.row && (
            <Grid container spacing={1}>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Order</Typography><Typography>{detail.row.orderNumber}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Parent</Typography><Typography>{detail.row.parentName || '—'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Phone</Typography><Typography>{detail.row.parentPhone || '—'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Device</Typography><Typography>{detail.row.deviceId || '—'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Amount</Typography><Typography>{formatMoney(detail.row.totalAmount)}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Gateway</Typography><Typography>{detail.row.paymentGateway || '—'}</Typography></Grid>
              <Grid item xs={12}><Typography variant="caption" color="text.secondary">Gateway payment id</Typography><Typography>{detail.row.gatewayPaymentId || '—'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Sub start</Typography><Typography>{formatDateOnly(detail.row.subscriptionStart)}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Sub end</Typography><Typography>{formatDateOnly(detail.row.subscriptionEnd)}</Typography></Grid>
              <Grid item xs={12}><Typography variant="caption" color="text.secondary">School</Typography><Typography>{detail.row.schoolName || '—'}</Typography></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminBillingReports;
